import { createColumnHelper } from '@tanstack/react-table';
import cn from 'classnames';
import { useMemo } from 'react';
import { useSuspenseHostsDetailsQueries } from '#api/hosts.queries.ts';
import { AddCircleIcon } from '#assets/ReactSvg.ts';
import { StatusDot } from '#components/atoms/icons/StatusDot.tsx';
import { Link } from '#components/atoms/link/Link.tsx';
import { IpAddressPillList } from '#components/patterns/ip-address-pill-list/IpAddressPillList.tsx';
import { NameCell } from '#components/patterns/table/name-cell/NameCell.tsx';
import { Table } from '#components/patterns/table/Table.tsx';
import type { HostStatus } from '#dn-types/host.ts';
import type { RouteListItem } from '#dn-types/route.ts';
import { useAppLocation } from '#hooks/useAppLocation.ts';
import { InlineIconLink } from '#screens/hosts/hosts-table/InlineIconLink.tsx';
import { computeHostStatus } from '#util/hostStatus.ts';
import { RoutesActionMenu } from './RoutesActionMenu.tsx';
import styles from './RoutesList.module.css';

const columnHelper = createColumnHelper<RouteListItem>();

type Props = {
  data: RouteListItem[];
  isFetching: boolean;
};

export function RoutesList({ data, isFetching }: Props) {
  const location = useAppLocation();
  const hostIds = data.map((r) => r.routerHostID).filter(Boolean);
  const hostQueries = useSuspenseHostsDetailsQueries(hostIds);
  const hosts = hostQueries.map((query) => query.data.data);
  const dataUpdatedAt = hostQueries[0]?.dataUpdatedAt;

  const columns = useMemo(
    () => [
      columnHelper.accessor('name', {
        header: 'Name',
        size: 170,
        cell: (info) => {
          const name = info.getValue();
          const { id, description } = info.row.original;
          return <NameCell name={name} nameTo={id} secondRow={<span className="truncate">{description}</span>} />;
        },
        meta: {
          truncate: true,
        },
      }),
      columnHelper.accessor('routerHostID', {
        id: 'routerHost',
        header: 'Router host',
        size: 150,
        meta: {
          truncate: true,
          className: styles.RoutesList_routerHost,
        },
        cell: (info) => {
          const hostId = info.getValue();
          const host = hosts.find((h) => h.id === hostId);
          let status: HostStatus | undefined;
          if (host && dataUpdatedAt) {
            status = computeHostStatus(host, new Date(dataUpdatedAt));
          }

          if (hostId) {
            const hostName = hosts.find((h) => h.id === hostId)?.name ?? hostId;
            return (
              <div className="flex align-center">
                {status ?
                  <StatusDot className="shrink-0 mr-8" status={status} />
                : null}
                <Link className="a inline truncate" to={`/hosts/${hostId}`} state={{ from: location }}>
                  {hostName}
                </Link>
              </div>
            );
          }
          const { id } = info.row.original;
          return (
            <div className="flex align-center">
              Missing router
              <InlineIconLink title="Add router" to={`/routes/${id}`} state={{ from: location }}>
                <AddCircleIcon />
              </InlineIconLink>
            </div>
          );
        },
      }),
      columnHelper.accessor('routerHostID', {
        id: 'routerIPAddress',
        header: 'Router host IP',
        size: 180,
        meta: {
          className: styles.RoutesList_hostIp,
        },
        cell: (info) => {
          const hostId = info.getValue();
          if (hostId) {
            const ipAddresses = hosts.find((h) => h.id === hostId)?.ipAddresses;
            return ipAddresses ? <IpAddressPillList ipAddresses={ipAddresses} /> : null;
          }
        },
      }),
      columnHelper.accessor('routableCIDRs', {
        header: 'Routable CIDRs',
        size: 180,
        cell(info) {
          const cidrs = Object.keys(info.getValue());

          return cidrs.join(', ');
        },
        meta: {
          truncate: true,
          className: styles.RoutesList_routableCIDRs,
        },
      }),
      columnHelper.display({
        id: 'tag-action-menu',
        header: '',
        size: 34,
        cell(info) {
          const id = info.row.original.id;

          return <div className="flex justify-end">{<RoutesActionMenu id={id} />}</div>;
        },
      }),
    ],
    [hosts, location, dataUpdatedAt]
  );

  return <Table data={data} columns={columns} className={cn({ 'opacity-dim': isFetching })} rowHeight="tall" />;
}
